import { useRef, useEffect, useCallback, useMemo } from 'preact/hooks'
import { useStateWithCallback } from './useStateWithCallback'

export const PRE_ENTER = 'preEnter'
export const ENTERING = 'entering'
export const ENTERED = 'entered'
export const PRE_EXIT = 'preExit'
export const EXITING = 'exiting'
export const EXITED = 'exited'
export const UNMOUNTED = 'unmounted'

export type TransitionState =
  | typeof PRE_ENTER
  | typeof ENTERING
  | typeof ENTERED
  | typeof PRE_EXIT
  | typeof EXITING
  | typeof EXITED
  | typeof UNMOUNTED

export interface TransitionProps {
  initialEntered?: boolean
  mountOnEnter?: boolean
  unmountOnExit?: boolean
  timeout?: number | { enter: number; exit: number }
  preEnter?: boolean
  preExit?: boolean
  enter?: boolean
  exit?: boolean
}

export const useTransition = ({
  initialEntered = true,
  mountOnEnter = false,
  unmountOnExit = false,
  preEnter = true,
  preExit = true,
  enter = true,
  exit = true,
  timeout,
}: TransitionProps = {}): [TransitionState, (toEnter?: boolean) => void, () => void] => {
  const [state, setState] = useStateWithCallback<TransitionState>(() => {
    return initialEntered ? ENTERED : mountOnEnter ? UNMOUNTED : EXITED
  })
  const latestState = useRef(state)
  const timeoutId = useRef<number>()

  const [enterTimeout, exitTimeout] = useMemo(() => {
    return typeof timeout === 'object' ? [timeout.enter, timeout.exit] : [timeout, timeout]
  }, [timeout])

  const transitState = useCallback(
    (newState: TransitionState) => {
      const cancelPending = () => window.clearTimeout(timeoutId.current)
      const scheduleNext = (nextState: TransitionState, delay: number) => {
        timeoutId.current = window.setTimeout(() => transitState(nextState), delay)
      }

      if (newState === EXITED && unmountOnExit) {
        newState = UNMOUNTED
      }

      cancelPending()
      setState((latestState.current = newState), () => {
        switch (newState) {
          case PRE_ENTER:
            transitState(ENTERING)
            break
          case PRE_EXIT:
            transitState(EXITING)
            break
        }
      })

      switch (newState) {
        case ENTERING:
          if (enterTimeout >= 0) {
            scheduleNext(ENTERED, enterTimeout)
          }
          break
        case EXITING:
          if (exitTimeout >= 0) {
            scheduleNext(EXITED, exitTimeout)
          }
          break
      }
    },
    [setState, enterTimeout, exitTimeout, unmountOnExit],
  )

  const endTransition = useCallback(() => {
    switch (latestState.current) {
      case PRE_ENTER:
      case ENTERING:
        transitState(ENTERED)
        break
      case PRE_EXIT:
      case EXITING:
        transitState(EXITED)
        break
    }
  }, [transitState])

  const toggle = useCallback(
    (toEnter?: boolean) => {
      const enterStage = [PRE_ENTER, ENTERING, ENTERED].includes(latestState.current)
      if (typeof toEnter !== 'boolean') {
        toEnter = !enterStage
      }

      if (toEnter && !enterStage) {
        transitState(enter ? (preEnter ? PRE_ENTER : ENTERING) : ENTERED)
      } else if (!toEnter && enterStage) {
        transitState(exit ? (preExit ? PRE_EXIT : EXITING) : EXITED)
      }
    },
    [enter, exit, preEnter, preExit, transitState],
  )

  useEffect(() => () => window.clearTimeout(timeoutId.current), [])

  return [state, toggle, endTransition]
}
